"use client";
import React, { createContext, useContext, useEffect, useMemo } from "react";
import {
  PARTNER_ORIGIN_COOKIE,
  PARTNER_ORIGIN_MAX_AGE,
  encodePartnerOrigin,
  parsePartnerOrigin,
} from "@/utils/partner/partnerOrigin";

/**
 * Client-side partner storefront context.
 *
 * A server layout resolves the partner profile (see resolvePartner.js) and
 * seeds this provider with it, so nested client components (scoped search,
 * cart) can read the active partner WITHOUT another network round-trip.
 *
 * On the global sukuto.com site this provider is absent, so usePartner()
 * returns null and every consumer falls back to its normal, un-scoped
 * behaviour. This keeps the white-label scoping strictly additive.
 */

const PartnerContext = createContext(null);

export const PartnerProvider = ({ partner, children }) => {
  // Memoise so consumers don't re-render on unrelated parent updates.
  const value = useMemo(() => partner || null, [partner]);

  // Persist the booking-origin cookie for the active storefront. The shared
  // /cart and /booking routes live outside this provider and read that cookie
  // (JourneyChrome) to stay white-labelled; without this they only branded
  // after a search wrote the cookie, so Home -> Cart with no search leaked to
  // global Sukuto chrome. Being on the storefront IS the partner context, so we
  // sync it here too. Leak-prevention is unchanged: a later global search
  // clears it, and booking/confirm clears it once the booking is placed.
  useEffect(() => {
    if (value) syncPartnerBookingOrigin(value);
  }, [value]);

  return (
    <PartnerContext.Provider value={value}>{children}</PartnerContext.Provider>
  );
};

/**
 * Returns the active partner profile, or null when not inside a partner
 * storefront. Consumers must handle the null case (global-site behaviour).
 */
export function usePartner() {
  return useContext(PartnerContext);
}

/**
 * Convenience hook returning just the active partner id (or null). Used by the
 * scoped search to inject a `partners` filter and by the cart/checkout to tag
 * the booking platform.
 */
export function usePartnerId() {
  const partner = useContext(PartnerContext);
  return partner?.id ?? null;
}

/**
 * Additively scope a search/index payload to a single partner.
 *
 * The backend (SearchController) reads partner scoping from `filters.partners`
 * (an array), so this nests the id there without disturbing any other filters
 * already present (areas/brands/etc). When partnerId is falsy the payload is
 * returned unchanged, keeping global sukuto.com search behaviour identical.
 *
 * @param {object} apiData search/index POST body
 * @param {number|string|null|undefined} partnerId
 * @returns {object}
 */
export function withPartnerFilter(apiData, partnerId) {
  if (!partnerId) return apiData;
  return {
    ...apiData,
    filters: { ...(apiData?.filters || {}), partners: [partnerId] },
  };
}

/**
 * City list scoped to the active partner storefront.
 *
 * The partner profile (data/partner-by-slug) carries `cities` — the distinct
 * active cities where the partner has bookable inventory — in the same
 * { id, name, ... } shape the global `data/cities` endpoint returns. On a
 * partner storefront the city picker must offer ONLY these, not the global
 * list. Returns null off-storefront (or when empty) so callers fall back to
 * the normal global `data/cities` fetch with behaviour unchanged.
 *
 * @param {object|null|undefined} partner
 * @returns {Array|null}
 */
export function partnerCityList(partner) {
  return Array.isArray(partner?.cities) && partner.cities.length
    ? partner.cities
    : null;
}

/**
 * Booking-origin cookie (PLATFORM_PARTNER_WEB attribution).
 *
 * The /cart and /booking routes live OUTSIDE the /partner/<slug>/ route group,
 * so they cannot read this PartnerProvider. To still attribute a checkout to the
 * storefront it started from, the partner-aware search forms write the active
 * partner to a JS-readable cookie whenever a search runs, and clear it on a
 * global (un-scoped) search. Because a booking is always preceded by a search,
 * the cookie reflects the LATEST search origin = the checkout origin, which
 * keeps attribution leak-free: a later global search overwrites a stale partner
 * origin. The cart reads it at booking/confirm time to send bookingPlatform =
 * PLATFORM_PARTNER_WEB and clears it once the booking row is created.
 *
 * The cookie now holds { id, slug } (see utils/partner/partnerOrigin.js): the id
 * keeps attribution parity, and the slug lets the shared routes resolve the
 * partner server-side to render branded chrome (JourneyChrome). It is not
 * sensitive; the backend independently derives the partner from the cart's
 * vehicles, so this is purely an origin tag.
 */
export const PARTNER_WEB_BOOKING_PLATFORM = 10; // common\models\Constant::PLATFORM_PARTNER_WEB

/**
 * Record (or clear) the booking origin to match the partner the current search
 * was run under. Pass the active partner (or null/undefined on the global site).
 */
export function syncPartnerBookingOrigin(partner) {
  if (typeof document === "undefined") return;
  const value = encodePartnerOrigin(partner);
  if (value) {
    document.cookie = `${PARTNER_ORIGIN_COOKIE}=${value}; path=/; max-age=${PARTNER_ORIGIN_MAX_AGE}; samesite=lax`;
  } else {
    clearPartnerBookingOrigin();
  }
}

/**
 * Read the active booking-origin partner identifier (client-only). Returns a
 * truthy id (or slug, for parity) when the current checkout started from a
 * partner storefront, else null. The cart only needs presence to set the
 * partner-web booking platform.
 */
export function readPartnerBookingOrigin() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)sukuto_partner_origin=([^;]*)/);
  if (!match) return null;
  const origin = parsePartnerOrigin(match[1]);
  return origin?.id ?? origin?.slug ?? null;
}

/**
 * Read just the storefront slug of the active booking origin (client-only), or
 * null when there is none (global site) or the cookie predates the slug (legacy
 * id-only) format. The shared /cart and /booking routes use this to keep their
 * own back/continue links inside the partner storefront (e.g. "Add more
 * vehicles" -> /partner/<slug>/search instead of the global /search). A null
 * return yields the unchanged global path, so sukuto.com behaviour is identical.
 */
export function readPartnerOriginSlug() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)sukuto_partner_origin=([^;]*)/);
  if (!match) return null;
  return parsePartnerOrigin(match[1])?.slug ?? null;
}

/** Clear the booking-origin cookie (after a booking is placed / on global search). */
export function clearPartnerBookingOrigin() {
  if (typeof document === "undefined") return;
  document.cookie = `${PARTNER_ORIGIN_COOKIE}=; path=/; max-age=0; samesite=lax`;
}

export default PartnerContext;
